import { normalizeArrayResponse } from "@/data/source/response-normalizer";
import { URLList } from "@/data/url_list";
import { createLogger, withApiLogging } from "@/utils/logger/logger";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATResult } from "@sadtx/sat-error";

export class PatientSource {
  private readonly logger = createLogger("PatientSource");

  constructor(private readonly client: FetchApiClientMethod) {}

  async fetchPatients(): Promise<SATResult<unknown[], SATError>> {
    return this.fetchPatientList(URLList.PATIENT.LIST);
  }

  async fetchPatientsByDoctor(): Promise<SATResult<unknown[], SATError>> {
    return this.fetchPatientList(URLList.PATIENT.LIST_BY_DOCTOR);
  }

  async fetchPatientsByAdmin(): Promise<SATResult<unknown[], SATError>> {
    return this.fetchPatientList(URLList.PATIENT.LIST_BY_ADMIN);
  }

  private async fetchPatientList(url: string): Promise<SATResult<unknown[], SATError>> {
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(normalizeArrayResponse(data, PATIENT_LIST_RESPONSE_KEYS));
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async fetchPatient(uuid: string): Promise<SATResult<unknown, SATError>> {
    const url = `${URLList.PATIENT.DETAIL}/${uuid}`;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(data);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async updatePatient(uuid: string, payload: Record<string, unknown>): Promise<SATResult<void, SATError>> {
    const url = `${URLList.PATIENT.UPDATE}/${uuid}`;
    const requestPayload = patientSavePayloadForApi(payload, { isoBirthDate: true });
    try {
      await withApiLogging(this.logger, { method: "PUT", url, payload: requestPayload }, async () => {
        const response = await this.client.put(url, requestPayload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async createPatient(payload: Record<string, unknown>): Promise<SATResult<void, SATError>> {
    const url = URLList.PATIENT.CREATE;
    const requestPayload = patientSavePayloadForApi(payload);
    try {
      await withApiLogging(this.logger, { method: "POST", url, payload: requestPayload }, async () => {
        const response = await this.client.post(url, requestPayload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async resendAccountSetupSms(uuid: string): Promise<SATResult<void, SATError>> {
    const url = `${URLList.PATIENT.RESEND_ACCOUNT_SETUP_SMS}/${uuid}/account-setup-sms/resend`;
    try {
      await withApiLogging(this.logger, { method: "POST", url }, async () => {
        const response = await this.client.post(url);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async deactivatePatient(uuid: string): Promise<SATResult<void, SATError>> {
    const url = `${URLList.PATIENT.DEACTIVATE}/${uuid}/deactivate`;
    try {
      await withApiLogging(this.logger, { method: "DELETE", url }, async () => {
        const response = await this.client.delete(url);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async activatePatient(uuid: string): Promise<SATResult<void, SATError>> {
    const url = `${URLList.PATIENT.ACTIVATE}/${uuid}/activate`;
    try {
      await withApiLogging(this.logger, { method: "PUT", url }, async () => {
        const response = await this.client.put(url);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }
}

const PATIENT_LIST_RESPONSE_KEYS = [
  "patientList",
  "patientInfoList",
  "patientUserList",
  "patients",
  "dataList",
  "items",
  "list",
  "result",
  "content",
  "data",
] as const;

function patientSavePayloadForApi(
  payload: Record<string, unknown>,
  options: { isoBirthDate?: boolean } = {},
): Record<string, unknown> {
  const detail = asRecord(payload.detail);
  const birthDate = payload.birthDate ?? payload.birthday ?? detail.birthDate ?? detail.birthday;
  return withoutUndefined({
    fullName: payload.fullName ?? payload.name,
    phone: payload.phone,
    email: payload.email,
    birthDate: options.isoBirthDate ? toIsoBirthDateString(birthDate) : birthDate,
    gender: payload.gender ?? detail.gender,
    memo: payload.memo ?? detail.memo,
  });
}

function toIsoBirthDateString(value: unknown): unknown {
  if (typeof value !== "string") return value;

  const trimmedValue = value.trim();
  if (!trimmedValue) return value;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
    return new Date(`${trimmedValue}T00:00:00.000Z`).toISOString();
  }

  const parsedDate = new Date(trimmedValue);
  if (Number.isNaN(parsedDate.getTime())) return value;
  return parsedDate.toISOString();
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function withoutUndefined(record: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined));
}
